import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { PackagePlus, Loader2 } from "lucide-react";

const RM_REASONS = ["OPENING_STOCK", "STOCK_ADJUSTMENT", "PHYSICAL_COUNT_CORRECTION", "OTHER_APPROVED_ADJUSTMENT"];
const FG_REASONS = ["OPENING_STOCK", "STOCK_ADJUSTMENT", "PHYSICAL_COUNT_CORRECTION", "RETURN", "OTHER_APPROVED_ADJUSTMENT"];

// mode: "rm" | "fg". item: {ingredientId,name,unit} or {productId,sku,productName,unit}
export function AddStockDialog({ open, onOpenChange, mode = "rm", item = null, onDone }) {
  const isFg = mode === "fg";
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const reasons = isFg ? FG_REASONS : RM_REASONS;
  const name = isFg ? (item?.productName || item?.name) : item?.name;
  const idLabel = isFg ? (item?.productId) : (item?.ingredientId);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!(Number(form.qty) > 0)) return toast.error("Enter a quantity greater than zero");
    setBusy(true);
    try {
      if (isFg) {
        await api.post("/finished-goods/add-stock", {
          productId: item?.productId, sku: item?.sku, qty: Number(form.qty),
          reason: form.reason || "OPENING_STOCK", batchNo: form.batchNo || "",
          mfgDate: form.mfgDate || "", expiryDate: form.expiryDate || "",
          reference: form.reference || "", remarks: form.remarks || "",
        });
      } else {
        await api.post("/inventory/add-stock", {
          ingredientId: item?.ingredientId, qty: Number(form.qty),
          reason: form.reason || "OPENING_STOCK", rate: form.rate ? Number(form.rate) : undefined,
          reference: form.reference || "", remarks: form.remarks || "",
        });
      }
      toast.success("Stock added via ledger");
      setForm({}); onOpenChange(false); onDone && onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Add stock failed")); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-lg" data-testid="add-stock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PackagePlus className="h-4 w-4 text-amber-700" /> + Add Stock</DialogTitle>
          <DialogDescription className="text-xs">
            {name ? <>Ledger-backed stock movement for <b>{name}</b> ({idLabel}).</> : "Ledger-backed stock movement."} Current stock is never overwritten.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Quantity {item?.unit ? `(${item.unit})` : ""}</Label>
              <Input type="number" min="0" data-testid="add-stock-qty" value={form.qty || ""} onChange={(e) => set("qty", e.target.value)} className="h-9" /></div>
            <div><Label className="text-xs">Reason</Label>
              <Select value={form.reason || "OPENING_STOCK"} onValueChange={(v) => set("reason", v)}>
                <SelectTrigger data-testid="add-stock-reason" className="h-9 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{reasons.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          {isFg && (
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Batch ID</Label><Input data-testid="add-stock-batch" placeholder="blank = not recorded" value={form.batchNo || ""} onChange={(e) => set("batchNo", e.target.value)} className="h-9" /></div>
              <div><Label className="text-xs">MFG Date</Label><Input type="date" value={form.mfgDate || ""} onChange={(e) => set("mfgDate", e.target.value)} className="h-9" /></div>
              <div><Label className="text-xs">Expiry</Label><Input type="date" value={form.expiryDate || ""} onChange={(e) => set("expiryDate", e.target.value)} className="h-9" /></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {!isFg && <div><Label className="text-xs">Rate (₹, optional)</Label><Input type="number" value={form.rate || ""} onChange={(e) => set("rate", e.target.value)} className="h-9" /></div>}
            <div><Label className="text-xs">Invoice / Reference</Label><Input data-testid="add-stock-ref" value={form.reference || ""} onChange={(e) => set("reference", e.target.value)} className="h-9" /></div>
          </div>
          <div><Label className="text-xs">Remarks</Label><Input data-testid="add-stock-remarks" value={form.remarks || ""} onChange={(e) => set("remarks", e.target.value)} className="h-9" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="add-stock-submit" className="gap-2 bg-amber-700 hover:bg-amber-800" disabled={busy} onClick={submit}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Add Stock
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
